import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, Search, Globe } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useCurrency, CURRENCIES, SUPPORTED_CURRENCIES } from '../i18n/CurrencyContext';import GB from 'country-flag-icons/react/3x2/GB';
import ES from 'country-flag-icons/react/3x2/ES';
import FR from 'country-flag-icons/react/3x2/FR';
import IT from 'country-flag-icons/react/3x2/IT';
import DE from 'country-flag-icons/react/3x2/DE';
import BR from 'country-flag-icons/react/3x2/BR';
import PL from 'country-flag-icons/react/3x2/PL';
import RU from 'country-flag-icons/react/3x2/RU';
import UA from 'country-flag-icons/react/3x2/UA';
import CN from 'country-flag-icons/react/3x2/CN';
import JP from 'country-flag-icons/react/3x2/JP';
import { CataloniaFlag } from './flags/CataloniaFlag';

const navItems = [];

const languages = [
  { code: 'en', label: 'English',         Flag: GB },
  { code: 'es', label: 'Español',         Flag: ES },
  { code: 'ca', label: 'Català',          Flag: CataloniaFlag },
  { code: 'fr', label: 'Français',        Flag: FR },
  { code: 'de', label: 'Deutsch',         Flag: DE },
  { code: 'it', label: 'Italiano',        Flag: IT },
  { code: 'pt', label: 'Português',       Flag: BR },
  { code: 'pl', label: 'Polski',          Flag: PL },
  { code: 'uk', label: 'Українська',      Flag: UA },
  { code: 'ru', label: 'Русский',         Flag: RU },
  { code: 'zh', label: '中文',             Flag: CN },
  { code: 'ja', label: '日本語',           Flag: JP },
];

// Default currency per language (applied on language switch; user can still override afterward).
const LANG_TO_CURRENCY = {
  en: 'GBP', es: 'EUR', ca: 'EUR', fr: 'EUR', de: 'EUR', it: 'EUR',
  pt: 'EUR', pl: 'EUR', uk: 'EUR', ru: 'EUR', zh: 'CNY', ja: 'JPY',
};

export function Header({ children, showSearch, searchQuery, onSearchChange, onSearchSubmit }) {
  return <HeaderInner showSearch={showSearch} searchQuery={searchQuery} onSearchChange={onSearchChange} onSearchSubmit={onSearchSubmit}>{children}</HeaderInner>;
}

// Pure memoized implementation — only re-renders when its serializable
// props actually change. Since `onSearchChange` / `onSearchSubmit` identity
// changes on every parent render, we wrap the inner body in `React.memo`
// with a custom equality check that ignores function identity — the parent
// callbacks always read fresh state via closures when invoked.
const HeaderInner = React.memo(
  function HeaderInner({ children, showSearch, searchQuery, onSearchChange, onSearchSubmit }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [globeOpen, setGlobeOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [wishlistPop, setWishlistPop] = useState(false);
  const location = useLocation();
  const { wishlist, removeFromWishlist } = useWishlist();
  const navigate = useNavigate();
  const { lang, setLanguage, t } = useLanguage();
  const { currency, setCurrency, formatPrice } = useCurrency();
  const langRef = useRef(null);
  const currencyRef = useRef(null);
  const globeRef = useRef(null);
  const wishlistRef = useRef(null);
  const prevWishlistLength = useRef(wishlist.length);

  // Check if we're on Explore page
  const isExplorePage =
    location.pathname === '/Explore' || location.pathname.startsWith('/experience/');
  // Sitges Guide page — gets a mobile search bar inside the header (non-sticky)
  // identical in style to the homepage's sticky search bar.
  const isSitgesGuidePage = location.pathname === '/Sitges-Guide';
  const [guideSearchQuery, setGuideSearchQuery] = useState('');
  const handleGuideSearchSubmit = (e) => {
    e.preventDefault();
    const q = guideSearchQuery.trim();
    if (q) {
      navigate(`/Explore?search=${encodeURIComponent(q)}`);
    } else {
      navigate('/Explore');
    }
  };

  // Reset the guide search input every time the user leaves the Sitges Guide
  // page so the field doesn't keep stale text on the next visit.
  useEffect(() => {
    if (!isSitgesGuidePage) setGuideSearchQuery('');
  }, [isSitgesGuidePage]);

  // Mobile keyboard dismiss — when the user actually drags a finger to scroll
  // the Sitges Guide page, blur the focused search input so the on-screen
  // keyboard collapses (matches the homepage / Explore behavior). We listen
  // to `touchmove` (real finger motion) only — scroll events fire on layout
  // reflows (address bar, keyboard) which would otherwise close the keyboard
  // the moment the user taps the input.
  useEffect(() => {
    if (!isSitgesGuidePage) return;
    const dismissKeyboard = () => {
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        el.blur();
      }
    };
    window.addEventListener('touchmove', dismissKeyboard, { passive: true });
    return () => window.removeEventListener('touchmove', dismissKeyboard);
  }, [isSitgesGuidePage]);
  
  // Check if we're on a page with hero background image (transparent header)
  const pagesWithHeroImage = ['/'];
  const isHeroPage = pagesWithHeroImage.includes(location.pathname);

  // Pages with filter bars that should have non-sticky header on mobile
  const isFilterPage =
    location.pathname === '/Explore' || location.pathname.startsWith('/experience/');

  // Pages where the mobile header uses the "smart sticky" behavior:
  // hides while scrolling down, slides back in while scrolling up.
  // Inspired by Twitter/Medium mobile. Desktop is unaffected — the header
  // stays permanently fixed there.
  const isSmartStickyPage =
    location.pathname === '/Explore' ||
    location.pathname === '/Sitges-Guide' ||
    location.pathname.startsWith('/experience/');

  // Pages that should have shadow when scrolled
  const pagesWithShadow = ['/'];
  // Pages that should always show the shadow (e.g. static content pages)
  const pagesWithAlwaysShadow = ['/Sitges-Guide', '/privacy-policy', '/cookie-policy', '/legal-notice'];

  // Get current language object
  const currentLang = languages.find(l => l.code === lang) || languages[0];

  // Trigger pop animation when wishlist length increases
  useEffect(() => {
    if (wishlist.length > prevWishlistLength.current) {
      setWishlistPop(true);
      setTimeout(() => setWishlistPop(false), 400);
    }
    prevWishlistLength.current = wishlist.length;
  }, [wishlist.length]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      // Close any open header dropdown the moment the user starts scrolling.
      setWishlistOpen(false);
      setGlobeOpen(false);
      setLangOpen(false);
      setCurrencyOpen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Smart-sticky pin state for mobile on Explore / Sitges-Guide pages.
  //   'unpinned' → desktop / non-applicable pages: header sits in its
  //                natural flow (or absolute over hero on the homepage).
  //   'visible'  → header is `fixed top-0` and slid into view (translateY 0).
  //   'hidden'   → header is `fixed top-0` and slid out (translateY -100%).
  // Option A: on mobile + smart-sticky pages we keep the header ALWAYS
  // `position: fixed` and only animate `transform: translateY(...)` —
  // CSS can animate transforms cleanly, so the slide-in / slide-out is
  // deterministic and never desyncs from the FilterBar (which is also
  // permanently fixed). PinState only oscillates between 'visible' and
  // 'hidden' here; we never go back to 'unpinned' mid-session.
  const [pinState, setPinState] = useState(() => {
    if (typeof window === 'undefined') return 'unpinned';
    const isMobileMQ = window.matchMedia('(max-width: 1023px)').matches;
    const p = window.location.pathname;
    const isSmartSticky =
      p === '/Explore' || p === '/Sitges-Guide' || p.startsWith('/experience/');
    return isMobileMQ && isSmartSticky ? 'visible' : 'unpinned';
  });

  // Track whether we're on a mobile viewport (< lg breakpoint = 1024px).
  // Used to force the header to stay transparent on the mobile homepage
  // regardless of scroll / globe / wishlist state \u2014 on mobile the header is
  // absolutely positioned and scrolls away with the hero image, so we never
  // end up with a transparent bar sitting over white content.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  // Smart-sticky pin behaviour (Option A). Header is always `position:
  // fixed` on smart-sticky mobile pages — we only flip between 'visible'
  // (translateY 0) and 'hidden' (translateY -100%) so CSS can animate the
  // slide cleanly. No position swap, no rAF dance, no desync with the
  // FilterBar.
  useEffect(() => {
    if (!isMobile || !isSmartStickyPage) {
      setPinState('unpinned');
      return undefined;
    }
    // Ensure we're in a fixed pin state on (re-)entry to a smart-sticky
    // page. If the previous state was 'unpinned' (e.g. user navigated
    // here from the homepage), promote to 'visible' so the header is
    // immediately fixed at top-0 with a smooth transform-only animation.
    setPinState((prev) => (prev === 'unpinned' ? 'visible' : prev));

    let lastY = window.scrollY;
    let last = pinState === 'unpinned' ? 'visible' : pinState;
    let upAccum = 0;
    // Small upward scroll is enough to reveal — bigger thresholds made
    // the reveal feel random / surprise-y to users.
    const UP_THRESHOLD = 50;
    let rafId = 0;
    let pendingY = lastY;
    const apply = (next) => {
      if (next === last) return;
      last = next;
      setPinState(next);
    };
    const compute = () => {
      rafId = 0;
      const y = pendingY;
      const delta = y - lastY;
      // At top of page, always keep the header visible.
      if (y < 1) {
        apply('visible');
        upAccum = 0;
        lastY = y;
        return;
      }
      // Ignore tiny jitter / overscroll bounces.
      if (Math.abs(delta) < 6) {
        lastY = y;
        return;
      }
      if (delta > 0) {
        // Scrolling DOWN — hide on first decisive downward motion.
        upAccum = 0;
        if (last === 'visible') apply('hidden');
      } else {
        // Scrolling UP — accumulate; once past the small threshold,
        // slide the header back in. Reset on direction flip.
        upAccum += -delta;
        if (upAccum >= UP_THRESHOLD) apply('visible');
      }
      lastY = y;
    };
    const onSmartScroll = () => {
      pendingY = window.scrollY;
      if (rafId) return;
      rafId = window.requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onSmartScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onSmartScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isSmartStickyPage]);

  // Expose pin state to siblings (e.g. Explore's sticky FilterBar) so they
  // can move *as a unit* with the header rather than animating independently
  // — independent animations look glitchy / desynced on touch devices.
  // Strategy: set a data attribute on <html> + CSS variables that drive
  // both the header and FilterBar from the same source.
  useEffect(() => {
    const root = document.documentElement;
    if (!isMobile || !isSmartStickyPage) {
      root.removeAttribute('data-pin-state');
      root.style.removeProperty('--smart-header-offset');
      return undefined;
    }
    root.setAttribute('data-pin-state', pinState);
    root.style.setProperty('--smart-header-offset', '84px');
    return () => {
      root.removeAttribute('data-pin-state');
      root.style.removeProperty('--smart-header-offset');
    };
  }, [pinState, isMobile, isSmartStickyPage]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (currencyRef.current && !currencyRef.current.contains(e.target)) setCurrencyOpen(false);
      // The globe panel is portaled to document.body, so we also need to check
      // if the click happened inside any element marked as the globe panel.
      const globePanel = document.querySelector('[data-testid="globe-panel"]');
      const insideGlobe =
        (globeRef.current && globeRef.current.contains(e.target)) ||
        (globePanel && globePanel.contains(e.target));
      if (!insideGlobe) setGlobeOpen(false);
      if (wishlistRef.current && !wishlistRef.current.contains(e.target)) setWishlistOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Determine header styling based on homepage and scroll state.  The mobile
  // globe panel is drawn over the header, so we drop transparent mode when any
  // header-level dropdown is open to keep everything legible.
  //
  // Exception \u2014 on the mobile homepage we force the header to stay transparent
  // regardless of scroll / globe / wishlist state. On mobile, the header is
  // absolutely positioned and scrolls away with the hero, so there's no risk
  // of a see-through bar ending up over white content.
  const forceTransparent = isHeroPage && isMobile;
  // Header drop-shadow logic. Always-shadow pages always show it; the
  // homepage shows it only after the user scrolls. Suppressed entirely on
  // the mobile homepage because the header is absolutely positioned there
  // and scrolls away with the hero — a shadow under it looks out of place.
  const shouldShowShadow =
    !forceTransparent && (
      pagesWithAlwaysShadow.includes(location.pathname) ||
      (pagesWithShadow.includes(location.pathname) && scrolled)
    );
  const isTransparent =
    forceTransparent ||
    (isHeroPage && !scrolled);
  const headerBg = isTransparent ? 'bg-transparent' : (scrolled ? 'bg-white/95 shadow-[0_1px_12px_rgba(15,59,99,0.08)]' : 'bg-white/95');
  const textColor = isTransparent ? 'text-white' : 'text-[#0F3B63]';
  const hoverBg = isTransparent ? 'hover:bg-white/10' : 'hover:bg-[#f0f7ff]';
  const activeBg = isTransparent ? 'bg-white/20' : 'bg-[#f0f7ff]';
  const activeTextColor = isTransparent ? 'text-white' : 'text-[#0184ff]';
  const iconColor = isTransparent ? 'text-white' : 'text-[#0F3B63]';
  const iconHoverBg = isTransparent ? 'hover:bg-white/10' : 'hover:bg-[#f7f8fa]';

  // Compose mobile-only positioning classes. When pinned (visible or hidden),
  // the header overlays the page as `fixed top-0`; otherwise it sits in its
  // natural flow (relative for Explore, absolute for everything else). Desktop
  // is always `lg:fixed lg:top-0 lg:left-0`.
  const isPinned = pinState !== 'unpinned';
  const mobilePos = isPinned
    ? 'fixed top-0 left-0 right-0'
    : (isFilterPage ? 'relative' : 'absolute top-0 left-0');

  return (
    <header
      data-testid="header"
      className={`${mobilePos} lg:fixed lg:top-0 lg:left-0 w-full z-50 transition-all duration-300 ease-out`}
      style={{
        background: isTransparent
          ? 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 100%)'
          : '#ffffff',
        backdropFilter: isTransparent ? 'none' : 'blur(12px)',
        boxShadow: shouldShowShadow ? '0 4px 18px rgba(15,59,99,0.10)' : 'none',
        borderBottom: 'none',
        // Slide the header off-screen when pinned-but-hidden. `lg:` resets to 0
        // so desktop never inherits the translate.
        transform: pinState === 'hidden' ? 'translateY(-100%)' : 'translateY(0)',
        willChange: 'transform',
      }}
    >
      <div className="max-w-[1280px] mx-auto px-3 sm:px-8 flex items-center justify-between" style={{ height: 84 }}>

        {/* Left Section: Logo + Search Bar */}
        <div className="flex items-center gap-4">
          <Link
            to="/"
            onClick={(e) => {
              // If the user is already on the homepage, do a full reload
              // instead of a no-op SPA navigation. Lets people refresh the
              // hero / featured rows by tapping the logo.
              if (location.pathname === '/') {
                e.preventDefault();
                window.location.reload();
              }
            }}
            className="flex items-center shrink-0 ml-[5px] sm:ml-[13px]"
            data-testid="header-logo"
          >
            <img src="/logo-bueno.png" alt="SitgesVibes" width="207" height="55" className="h-[55px] w-auto" />
          </Link>

          {/* Search Bar - Different style for Explore page */}
          {showSearch && isExplorePage ? (
            // Explore page: Icon on left, no button, live search
            <div className="hidden lg:block ml-[60px]">
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#2d5f8d] pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={onSearchChange}
                  onFocus={(e) => e.target.select()}
                  placeholder={t('search_experiences')}
                  className="w-[500px] h-[55px] pl-14 pr-12 text-[16px] font-bold text-[#1a4d7a] rounded-full
                             bg-white border border-[#1a4d7a]
                             placeholder:text-[#2d5f8d]/60 placeholder:font-medium
                             focus:outline-none focus:ring-2 focus:ring-[#0184ff]/20 focus:border-[#0184ff]
                             transition-all duration-200 shadow-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    data-testid="header-search-clear"
                    onClick={() => onSearchChange && onSearchChange({ target: { value: '' } })}
                    aria-label="Clear search"
                    className="absolute right-4 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full
                               flex items-center justify-center text-[#2d5f8d]
                               hover:bg-[#eaf3fa] hover:text-[#0184ff]
                               transition-colors duration-150"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
          ) : showSearch ? (
            // Homepage: Original style with Search button
            <div className="hidden lg:block ml-[60px]">
              <form onSubmit={onSearchSubmit} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={onSearchChange}
                  onFocus={(e) => e.target.select()}
                  placeholder={t('search_experiences')}
                  className="w-[500px] h-[55px] pl-6 pr-[100px] text-[16px] font-bold text-[#1a4d7a] rounded-full
                             bg-white border border-[#1a4d7a]
                             placeholder:text-[#2d5f8d]/60 placeholder:font-medium
                             focus:outline-none focus:ring-2 focus:ring-[#0184ff]/20 focus:border-[#0184ff]
                             transition-all duration-200 shadow-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                />
                <button
                  type="submit"
                  className="absolute right-[9px] top-1/2 -translate-y-1/2 
                             h-[40px] px-5 rounded-full
                             bg-[#3c84ff] hover:bg-[#2a6fe0]
                             flex items-center justify-center
                             transition-all duration-200
                             text-white font-semibold text-[14px]"
                  aria-label={t('search')}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {t('search')}
                </button>
              </form>
            </div>
          ) : null}
        </div>

        {/* Right Section: Wishlist + (mobile Globe OR desktop Currency/Language) */}
        <div className="flex items-center gap-1 sm:gap-1.5">

          {/* Wishlist button — always visible */}
          <div className="relative" ref={wishlistRef}>
            <button
              data-testid="wishlist-trigger"
              onClick={() => { setWishlistOpen(!wishlistOpen); setLangOpen(false); setCurrencyOpen(false); setGlobeOpen(false); }}
              className={`relative flex items-center justify-center h-[48px] w-[48px] rounded-lg
                          lg:flex-col lg:w-auto lg:h-auto lg:px-[10px] lg:py-2 lg:gap-1
                         ${iconColor} ${iconHoverBg} transition-all duration-200 ${
                           wishlistPop ? 'heart-pop' : ''
                         }`}
              style={isTransparent ? { color: '#ffffff' } : undefined}
              aria-label="Wishlist"
            >
              <Heart className={`h-[24px] w-[24px] lg:h-[26px] lg:w-[26px] ${
                wishlist.length > 0
                  ? (isTransparent ? 'fill-white/50 text-white' : 'fill-red-500/50 text-red-500')
                  : ''
              }`} />
              {wishlist.length > 0 && (
                <span
                  className="absolute top-1 right-1.5 text-[10px] font-bold leading-none pointer-events-none"
                  style={{ color: isTransparent ? '#ffffff' : '#ef4444' }}
                >
                  {wishlist.length}
                </span>
              )}
              <span className="hidden lg:block text-[13px] font-medium leading-none">{t('wishlist')}</span>
            </button>
            {wishlistOpen && (() => {
              const WishlistList = (
                <>
                  {wishlist.length === 0 ? (
                    <div className="p-8 text-center">
                      <Heart className="h-8 w-8 mx-auto" style={{ color: '#2d5f8d' }} />
                      <p className="text-sm mt-2 font-medium" style={{ color: '#2d5f8d' }}>{t('wishlist_empty')}</p>
                    </div>
                  ) : (
                    wishlist.map(exp => {
                      const title = exp[`title_${lang}`] || exp.title_en;
                      const handleItemClick = () => {
                        setWishlistOpen(false);
                        navigate(`/Explore?search=${encodeURIComponent(title)}`);
                        // Always land at the top of results. Pathname doesn't
                        // change when we're already on /Explore, so the
                        // global ScrollToTop effect wouldn't fire otherwise.
                        // rAF ensures the scroll lands *after* React
                        // re-renders the filtered grid, so the browser can't
                        // snap back to the previous scroll position.
                        requestAnimationFrame(() =>
                          window.scrollTo({ top: 0, behavior: 'instant' })
                        );
                      };
                      return (
                        <div
                          key={exp.id}
                          onClick={handleItemClick}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleItemClick(); }}
                          data-testid={`wishlist-item-${exp.id}`}
                          className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#f7f8fa] transition-colors"
                        >
                          <img
                            src={exp.image}
                            alt=""
                            width="80"
                            height="60"
                            className="w-20 aspect-[4/3] rounded-lg object-cover shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-[17px] font-bold leading-snug line-clamp-2"
                              style={{ color: '#1a4d7a' }}
                            >
                              {title}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
                                try { navigator.vibrate(15); } catch (_) { /* noop */ }
                              }
                              removeFromWishlist(exp.id);
                            }}
                            className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-[#2d5f8d]
                                       hover:bg-[#eaf3fa] hover:text-[#0184ff]
                                       transition-colors duration-150"
                            aria-label="Remove from wishlist"
                          >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                          </button>
                        </div>
                      );
                    })
                  )}
                </>
              );

              return (
                <>
                  {/* Mobile: portal + backdrop + centered modal */}
                  {createPortal(
                    <div
                      data-testid="wishlist-backdrop"
                      style={{ zIndex: 9999 }}
                      className="lg:hidden fixed inset-0 bg-black/40 flex items-start justify-center p-4 pt-[10vh] animate-[fadeIn_150ms_ease-out]"
                      onClick={() => setWishlistOpen(false)}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div
                        data-testid="wishlist-panel"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full max-w-[360px] bg-white rounded-2xl
                                   shadow-[0_20px_60px_rgba(0,0,0,0.25)]
                                   max-h-[80vh] overflow-y-auto
                                   animate-[scaleIn_180ms_ease-out]"
                      >
                        {WishlistList}
                      </div>
                    </div>,
                    document.body
                  )}

                  {/* Desktop: right-anchored dropdown matching the globe panel */}
                  <div
                    className="hidden lg:block absolute right-0 top-full mt-[30px] w-[360px] rounded-2xl border border-[#eee] shadow-[0_20px_60px_rgba(15,59,99,0.18)] max-h-[80vh] overflow-y-auto z-50"
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    {WishlistList}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Globe button — opens combined Currency + Language panel.
              Mobile: full-screen modal via portal. Desktop: right-anchored dropdown. */}
          <div className="relative" ref={globeRef}>
            <button
              data-testid="globe-trigger"
              onClick={() => { setGlobeOpen(!globeOpen); setWishlistOpen(false); setLangOpen(false); setCurrencyOpen(false); }}
              className={`flex items-center justify-center h-[48px] w-[48px] rounded-lg
                          lg:flex-col lg:w-auto lg:h-auto lg:px-[10px] lg:py-2 lg:gap-1
                          ${iconColor} ${iconHoverBg} transition-all duration-200`}
              style={isTransparent ? { color: '#ffffff' } : undefined}
              aria-label="Currency & language"
              aria-expanded={globeOpen}
            >
              <Globe className="h-[24px] w-[24px] lg:h-[26px] lg:w-[26px]" />
              <span className="hidden lg:block text-[13px] font-medium leading-none whitespace-nowrap">
                {lang.toUpperCase()}/{currency} {CURRENCIES[currency]?.symbol}
              </span>
            </button>

            {/* Mobile-only: portal backdrop + centered modal */}
            {globeOpen && createPortal(
              <div
                data-testid="globe-backdrop"
                style={{ zIndex: 9999 }}
                className="lg:hidden fixed inset-0 bg-black/40 flex items-start justify-center p-4 pt-[10vh] animate-[fadeIn_150ms_ease-out]"
                onClick={() => setGlobeOpen(false)}
                onMouseDown={(e) => {
                  // Prevent the document-level outside-click handler from
                  // firing before our onClick; we handle closing here.
                  e.stopPropagation();
                }}
              >
                <div
                  data-testid="globe-panel"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-full max-w-[360px] bg-white rounded-2xl
                             shadow-[0_20px_60px_rgba(0,0,0,0.25)]
                             max-h-[80vh] overflow-y-auto
                             animate-[scaleIn_180ms_ease-out]"
                >
                  {/* Language section */}
                  <div className="px-5 pt-5 pb-2">
                    <div className="grid grid-cols-2 gap-2">
                      {languages.map(l => {
                        const active = lang === l.code;
                        return (
                          <button
                            key={l.code}
                            data-testid={`language-option-${l.code}`}
                            onClick={() => {
                              setLanguage(l.code);
                              if (LANG_TO_CURRENCY[l.code]) setCurrency(LANG_TO_CURRENCY[l.code]);
                              setGlobeOpen(false);
                            }}
                            className={`h-11 px-3 rounded-xl flex items-center gap-2 text-[14px] transition-colors border
                                       ${active
                                         ? 'text-[#0184ff] bg-[#f0f7ff] border-[#cce4ff] font-medium'
                                         : 'text-[#2d5f8d] bg-white border-[#eee] hover:bg-[#f7f8fa]'}`}
                          >
                            <l.Flag className="h-3.5 w-5 shrink-0" />
                            <span className="truncate">{l.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-[#f0f0f0] mx-5 my-2" />

                  {/* Currency section */}
                  <div className="px-5 pt-2 pb-5">
                    <div className="grid grid-cols-2 gap-2">
                      {SUPPORTED_CURRENCIES.map(code => {
                        const info = CURRENCIES[code];
                        const active = currency === code;
                        return (
                          <button
                            key={code}
                            data-testid={`currency-option-${code}`}
                            onClick={() => { setCurrency(code); setGlobeOpen(false); }}
                            className={`h-11 px-3 rounded-xl flex items-center gap-2 text-[14px] transition-colors border
                                       ${active
                                         ? 'text-[#0184ff] bg-[#f0f7ff] border-[#cce4ff] font-medium'
                                         : 'text-[#2d5f8d] bg-white border-[#eee] hover:bg-[#f7f8fa]'}`}
                          >
                            <span className="w-5 text-center text-[14px] font-semibold shrink-0 text-[#1a4d7a]">{info.symbol}</span>
                            <span className="uppercase text-[12px] tracking-wide text-[#2d5f8d] shrink-0">{code}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Desktop-only: right-anchored dropdown with same grids (no backdrop) */}
            {globeOpen && (
              <div
                data-testid="globe-panel-desktop"
                className="hidden lg:block absolute right-0 top-full mt-[30px] w-[360px] bg-white rounded-2xl border border-[#eee] shadow-[0_20px_60px_rgba(15,59,99,0.18)] max-h-[80vh] overflow-y-auto z-50"
              >
                {/* Language section */}
                <div className="px-5 pt-5 pb-2">
                  <div className="grid grid-cols-2 gap-2">
                    {languages.map(l => {
                      const active = lang === l.code;
                      return (
                        <button
                          key={l.code}
                          data-testid={`language-option-desktop-${l.code}`}
                          onClick={() => {
                            setLanguage(l.code);
                            if (LANG_TO_CURRENCY[l.code]) setCurrency(LANG_TO_CURRENCY[l.code]);
                            setGlobeOpen(false);
                          }}
                          className={`h-11 px-3 rounded-xl flex items-center gap-2 text-[14px] transition-colors border
                                     ${active
                                       ? 'text-[#0184ff] bg-[#f0f7ff] border-[#cce4ff] font-medium'
                                       : 'text-[#2d5f8d] bg-white border-[#eee] hover:bg-[#f7f8fa]'}`}
                        >
                          <l.Flag className="h-3.5 w-5 shrink-0" />
                          <span className="truncate">{l.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="h-px bg-[#f0f0f0] mx-5 my-2" />

                {/* Currency section */}
                <div className="px-5 pt-2 pb-5">
                  <div className="grid grid-cols-2 gap-2">
                    {SUPPORTED_CURRENCIES.map(code => {
                      const info = CURRENCIES[code];
                      const active = currency === code;
                      return (
                        <button
                          key={code}
                          data-testid={`currency-option-desktop-${code}`}
                          onClick={() => { setCurrency(code); setGlobeOpen(false); }}
                          className={`h-11 px-3 rounded-xl flex items-center gap-2 text-[14px] transition-colors border
                                     ${active
                                       ? 'text-[#0184ff] bg-[#f0f7ff] border-[#cce4ff] font-medium'
                                       : 'text-[#2d5f8d] bg-white border-[#eee] hover:bg-[#f7f8fa]'}`}
                        >
                          <span className="w-5 text-center text-[14px] font-semibold shrink-0">{info.symbol}</span>
                          <span className="uppercase text-[12px] tracking-wide text-[#2d5f8d] shrink-0">{code}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>{/* /right section */}
      </div>

      {/* Mobile menu - removed since no nav items */}

      {/* Sitges Guide — mobile-only in-header search bar (NOT sticky).
          Visually identical to the homepage's sticky search bar. */}
      {isSitgesGuidePage && (
        <div className="lg:hidden">
          <div className="px-[9px] pb-2">
            <form onSubmit={handleGuideSearchSubmit} className="relative">
              <input
                type="text"
                value={guideSearchQuery}
                onChange={(e) => setGuideSearchQuery(e.target.value)}
                onFocus={(e) => e.target.select()}
                placeholder={t('search_experiences')}
                className="w-full h-[50px] pl-4 pr-[90px] text-[15px] font-bold text-[#1a4d7a] rounded-full
                           bg-white border border-[#1a4d7a]
                           placeholder:text-[#2d5f8d]/60 placeholder:font-medium
                           focus:outline-none focus:ring-2 focus:ring-[#0184ff]/20 focus:border-[#0184ff]
                           transition-all duration-200 shadow-sm"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="sitges-guide-header-search-input"
              />
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 
                           h-[42px] px-7 rounded-full
                           bg-[#3c84ff] hover:bg-[#2a6fe0]
                           flex items-center justify-center
                           transition-all duration-200
                           text-white font-semibold text-[14px]"
                aria-label={t('search')}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="sitges-guide-header-search-submit"
              >
                {t('search')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* FilterBar Section (if provided as children) */}
      {children && (
        <div className="bg-white border-t border-[#f0f0f0]">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-4">
            {children}
          </div>
        </div>
      )}
    </header>
  );
  },
  (prev, next) =>
    prev.showSearch === next.showSearch && prev.searchQuery === next.searchQuery,
);
